// Telegraphs (decision 0047: "ground markers projected onto the terrain: enemy attack zones that fill before the
// blow"; 0059 and 0063: unblockable attacks carry their own colour and a distinct shape and pattern, never colour
// alone). A telegraph is a circle, a cone or a line on the ground in front of an attacker, filling from the attacker
// outwards over its lead time, so it is full the moment the blow lands; then it flashes and fades.
//
// Projected on the terrain: each telegraph is a grid laid over its shape's bounds whose vertices take the ground's
// height from a function the game passes in (`heightAt(x, z)`), lifted a little, so it hugs slopes and steps rather
// than floating flat. The shape is cut in the fragment shader from the grid's local coordinates.
//
// One shader for every telegraph (each has its own material instance with the same program), so a game compiles it
// once at boot (`warm`) and a new telegraph in play never compiles a shader.
import * as THREE from "three";

/** A telegraph's shape, in metres and radians (the angle is the cone's full opening). */
export type TelegraphShape = { kind: "circle"; radius: number } | { kind: "cone"; radius: number; angle: number } | { kind: "line"; length: number; width: number };

/** How a class of telegraph looks, from the game (decision 0063: unblockable ones differ in pattern and edge too). */
export interface TelegraphStyle {
  /** The fill and edge colours. */
  fill: THREE.ColorRepresentation;
  edge: THREE.ColorRepresentation;
  /** Diagonal stripes (0: none), their spacing (m) and strength. */
  stripes: number;
  stripeSpacing: number;
  /** A second, inner edge ring (a double border) when true. */
  doubleEdge: boolean;
  /** Opacity of the unfilled zone, the filled part and the edge (0..1). */
  baseAlpha: number;
  fillAlpha: number;
  edgeAlpha: number;
  /** Seconds the marker flashes and fades after the blow lands. */
  linger: number;
}

/** One telegraph to show. */
export interface TelegraphSpec {
  shape: TelegraphShape;
  /** Where the attacker stands (world x, z; the height comes from the ground) and which way it faces (yaw: 0 faces
   *  +Z, growing towards +X). */
  origin: readonly [number, number, number];
  facing: number;
  /** Seconds from now until the blow is active (the fill reaches the edge then). */
  lead: number;
  /** The style's name (the game's table). */
  style: string;
  /** Who it belongs to (to cancel it when the attacker is hit out of the move). */
  owner?: number;
}

/** A live telegraph's state, for a harness. */
export interface TelegraphState {
  id: number;
  owner: number | null;
  shape: TelegraphShape["kind"];
  style: string;
  /** 0 at the start, 1 when the blow lands. */
  fill: number;
  /** "filling" until the blow, then "landed" while it lingers. */
  phase: "filling" | "landed";
  /** Where the grid's centre sits, and the ground's relief under the grid (highest minus lowest vertex, m): 0 on
   *  flat ground, more where the marker bends over a slope or a step. */
  centre: [number, number, number];
  relief: number;
}

const VERT = /* glsl */ `
attribute vec2 aLocal;
varying vec2 vLocal;
void main() {
  vLocal = aLocal;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

// uShape: 0 circle, 1 cone, 2 line. uSize: circle (r, 0), cone (r, half angle), line (length, half width).
const FRAG = /* glsl */ `
uniform int uShape;
uniform vec2 uSize;
uniform float uFill;
uniform float uFlash;
uniform float uFade;
uniform vec3 uFillColour;
uniform vec3 uEdgeColour;
uniform float uStripes;
uniform float uStripeSpacing;
uniform float uDouble;
uniform vec3 uAlpha; // base, fill, edge
varying vec2 vLocal;
void main() {
  // r: 0 at the attacker, 1 at the far edge; edge: distance (m) to the outline (0 outside)
  float r; float edgeDist;
  if (uShape == 2) {
    float u = abs(vLocal.x) / uSize.y;
    r = vLocal.y / uSize.x;
    if (u > 1.0 || r < 0.0 || r > 1.0) discard;
    edgeDist = min(min(uSize.y - abs(vLocal.x), vLocal.y), uSize.x - vLocal.y);
  } else {
    float d = length(vLocal);
    r = d / uSize.x;
    if (r > 1.0) discard;
    edgeDist = uSize.x - d;
    if (uShape == 1) {
      float a = abs(atan(vLocal.x, vLocal.y));
      if (a > uSize.y) discard;
      edgeDist = min(edgeDist, d * sin(uSize.y - a));
    }
  }
  float edge = 1.0 - smoothstep(0.05, 0.11, edgeDist);
  float inner = uDouble * (smoothstep(0.17, 0.2, edgeDist) - smoothstep(0.24, 0.27, edgeDist));
  float filled = 1.0 - smoothstep(uFill - 0.015, uFill, r);
  float front = smoothstep(uFill - 0.05, uFill, r) * (1.0 - smoothstep(uFill, uFill + 0.01, r)) * step(0.001, uFill);
  float stripe = uStripes * step(0.5, fract((vLocal.x + vLocal.y) / uStripeSpacing));
  float a = uAlpha.x * (1.0 + stripe) + filled * uAlpha.y * (1.0 + 0.6 * stripe) + front * 0.5 + (edge + inner) * uAlpha.z + uFlash * 0.6;
  vec3 c = mix(uFillColour, uEdgeColour, clamp(edge + inner + front, 0.0, 1.0));
  c = mix(c, vec3(1.0), uFlash * 0.5);
  gl_FragColor = vec4(c, clamp(a, 0.0, 1.0) * uFade);
}`;

function telegraphMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: {
      uShape: { value: 0 },
      uSize: { value: new THREE.Vector2(1, 0) },
      uFill: { value: 0 },
      uFlash: { value: 0 },
      uFade: { value: 1 },
      uFillColour: { value: new THREE.Color() },
      uEdgeColour: { value: new THREE.Color() },
      uStripes: { value: 0 },
      uStripeSpacing: { value: 0.5 },
      uDouble: { value: 0 },
      uAlpha: { value: new THREE.Vector3(0.1, 0.3, 0.8) },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -4,
    polygonOffsetUnits: -4,
    side: THREE.DoubleSide,
    toneMapped: false,
  });
}

/** The telegraph pool's options. */
export interface TelegraphOptions {
  /** The ground's height at (x, z): where the marker is draped. */
  heightAt: (x: number, z: number) => number;
  styles: Readonly<Record<string, TelegraphStyle>>;
  /** Grid cells per side (default 20): more hug the ground better. */
  cells?: number;
  /** Lift above the ground (m, default 0.04). */
  lift?: number;
}

/** Telegraphs for one scene: add `root` to the scene, `start` one per wind-up, `update(now)` once per frame on the
 *  sim clock (so the fill lands with the blow even through hit-stop and slow motion). */
export class Telegraphs {
  readonly root = new THREE.Group();
  private readonly o: Required<Pick<TelegraphOptions, "cells" | "lift">> & TelegraphOptions;
  private pool: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>[] = [];
  private live: { id: number; spec: TelegraphSpec; start: number; mesh: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>; centre: [number, number, number]; relief: number }[] = [];
  private next = 1;
  private now = 0;
  /** Telegraphs started so far, by style. */
  readonly started: Record<string, number> = {};

  constructor(o: TelegraphOptions) {
    this.o = { cells: 20, lift: 0.04, ...o };
    this.root.name = "vfx:telegraphs";
  }

  private take(): THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial> {
    const m = this.pool.pop();
    if (m) return m;
    const n = this.o.cells + 1;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * n * 3), 3));
    g.setAttribute("aLocal", new THREE.BufferAttribute(new Float32Array(n * n * 2), 2));
    const idx: number[] = [];
    for (let j = 0; j < this.o.cells; j++)
      for (let i = 0; i < this.o.cells; i++) {
        const a = j * n + i;
        idx.push(a, a + n, a + 1, a + 1, a + n, a + n + 1);
      }
    g.setIndex(idx);
    const mesh = new THREE.Mesh(g, telegraphMaterial());
    mesh.name = "vfx:telegraph";
    mesh.frustumCulled = false;
    mesh.renderOrder = 2;
    mesh.castShadow = false;
    mesh.receiveShadow = false;
    return mesh;
  }

  /** Lay a mesh's grid over the shape's bounds at the attacker, draped on the ground. Returns the centre and drape. */
  private lay(mesh: THREE.Mesh, s: TelegraphSpec): { centre: [number, number, number]; relief: number } {
    const shape = s.shape;
    // bounds in the attacker's frame: x right, y (local) forward
    const [x0, x1, y0, y1] =
      shape.kind === "line"
        ? [-shape.width / 2, shape.width / 2, 0, shape.length]
        : shape.kind === "cone"
          ? [-shape.radius * Math.sin(Math.min(Math.PI / 2, shape.angle / 2)), shape.radius * Math.sin(Math.min(Math.PI / 2, shape.angle / 2)), shape.angle / 2 > Math.PI / 2 ? -shape.radius * Math.sin(shape.angle / 2 - Math.PI / 2) : 0, shape.radius]
          : [-shape.radius, shape.radius, -shape.radius, shape.radius];
    const n = this.o.cells + 1;
    const pos = mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
    const loc = mesh.geometry.getAttribute("aLocal") as THREE.BufferAttribute;
    const fx = Math.sin(s.facing);
    const fz = Math.cos(s.facing);
    // right of a yaw: (-cos, sin) on (x, z)
    const rx = -Math.cos(s.facing);
    const rz = Math.sin(s.facing);
    let lo = Infinity;
    let hi = -Infinity;
    for (let j = 0; j < n; j++)
      for (let i = 0; i < n; i++) {
        const lx = x0 + ((x1 - x0) * i) / this.o.cells;
        const ly = y0 + ((y1 - y0) * j) / this.o.cells;
        const wx = s.origin[0] + rx * lx + fx * ly;
        const wz = s.origin[2] + rz * lx + fz * ly;
        const h = this.o.heightAt(wx, wz);
        const k = j * n + i;
        pos.setXYZ(k, wx, h + this.o.lift, wz);
        loc.setXY(k, lx, ly);
        lo = Math.min(lo, h);
        hi = Math.max(hi, h);
      }
    pos.needsUpdate = true;
    loc.needsUpdate = true;
    mesh.geometry.computeBoundingSphere();
    const cx = (x0 + x1) / 2;
    const cy = (y0 + y1) / 2;
    const wx = s.origin[0] + rx * cx + fx * cy;
    const wz = s.origin[2] + rz * cx + fz * cy;
    return { centre: [wx, this.o.heightAt(wx, wz), wz], relief: hi - lo };
  }

  /** Show a telegraph; returns its id. `now` is the sim time it starts at (default the last `update`'s). */
  start(s: TelegraphSpec, now = this.now): number {
    const style = this.o.styles[s.style];
    if (!style) throw new Error(`telegraph: no style "${s.style}"`);
    const mesh = this.take();
    const { centre, relief } = this.lay(mesh, s);
    const u = mesh.material.uniforms;
    const shape = s.shape;
    u.uShape!.value = shape.kind === "circle" ? 0 : shape.kind === "cone" ? 1 : 2;
    (u.uSize!.value as THREE.Vector2).set(shape.kind === "line" ? shape.length : shape.radius, shape.kind === "cone" ? shape.angle / 2 : shape.kind === "line" ? shape.width / 2 : 0);
    (u.uFillColour!.value as THREE.Color).set(style.fill);
    (u.uEdgeColour!.value as THREE.Color).set(style.edge);
    u.uStripes!.value = style.stripes;
    u.uStripeSpacing!.value = style.stripeSpacing;
    u.uDouble!.value = style.doubleEdge ? 1 : 0;
    (u.uAlpha!.value as THREE.Vector3).set(style.baseAlpha, style.fillAlpha, style.edgeAlpha);
    u.uFill!.value = 0;
    u.uFlash!.value = 0;
    u.uFade!.value = 1;
    mesh.visible = true;
    this.root.add(mesh);
    const id = this.next++;
    this.live.push({ id, spec: s, start: now, mesh, centre, relief });
    this.started[s.style] = (this.started[s.style] ?? 0) + 1;
    return id;
  }

  /** Remove a telegraph now (its attacker was interrupted), or every one an owner has. */
  cancel(id: number): void {
    const i = this.live.findIndex((l) => l.id === id);
    if (i < 0) return;
    const [l] = this.live.splice(i, 1);
    l!.mesh.removeFromParent();
    this.pool.push(l!.mesh);
  }

  cancelOwner(owner: number): void {
    for (const l of [...this.live]) if (l.spec.owner === owner) this.cancel(l.id);
  }

  /** Advance to sim time `now`: fill, then flash and fade after the blow. */
  update(now: number): void {
    this.now = now;
    for (const l of [...this.live]) {
      const style = this.o.styles[l.spec.style]!;
      const t = now - l.start;
      const u = l.mesh.material.uniforms;
      const lead = Math.max(1e-6, l.spec.lead);
      if (t < lead) {
        u.uFill!.value = Math.max(0, t / lead);
        u.uFlash!.value = 0;
        u.uFade!.value = 1;
      } else {
        const after = (t - lead) / Math.max(1e-6, style.linger);
        if (after >= 1) {
          this.cancel(l.id);
          continue;
        }
        u.uFill!.value = 1;
        u.uFlash!.value = 1 - after;
        u.uFade!.value = 1 - after * after;
      }
    }
  }

  /** The live telegraphs. */
  state(): TelegraphState[] {
    return this.live.map((l) => {
      const fill = l.mesh.material.uniforms.uFill!.value as number;
      return { id: l.id, owner: l.spec.owner ?? null, shape: l.spec.shape.kind, style: l.spec.style, fill, phase: this.now - l.start < l.spec.lead ? "filling" : "landed", centre: l.centre, relief: l.relief };
    });
  }

  /** A live telegraph's mesh (its vertices are world positions), for a check against the ground. */
  mesh(id: number): THREE.Mesh | null {
    return this.live.find((l) => l.id === id)?.mesh ?? null;
  }

  /** Show one of each shape (for a boot warm-up compile), returning a function that clears them. */
  warm(origin: readonly [number, number, number], style: string): () => void {
    const ids = [
      this.start({ shape: { kind: "circle", radius: 1 }, origin, facing: 0, lead: 1, style }),
      this.start({ shape: { kind: "cone", radius: 1, angle: 1 }, origin, facing: 0, lead: 1, style }),
      this.start({ shape: { kind: "line", length: 1, width: 0.5 }, origin, facing: 0, lead: 1, style }),
    ];
    // a warm-up isn't a telegraph shown
    this.started[style]! -= ids.length;
    if (this.started[style] === 0) delete this.started[style];
    return () => ids.forEach((id) => this.cancel(id));
  }

  /** Free every mesh. */
  dispose(): void {
    for (const l of [...this.live]) this.cancel(l.id);
    for (const m of this.pool) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this.pool = [];
    this.root.removeFromParent();
  }
}
